import {
    dollarsToCents,
    roundCentsToWholeDollarCents,
} from '@fruitful/fruitils';
import type {
    MembershipProfileBillsAnswer,
    MembershipProfileIncomeAnswer,
    ProfileAnswer,
} from '@fruitful/member-profile';
import { QuestionKey } from '@fruitful/member-profile';

import { type MembershipDetails } from '../../entities/entityTypes';
import { findMembershipProfileAnswerByQuestionKey } from '../../member-profile/utils/memberProfileUtils';

export interface ResolvedMoneyMapperDefaults {
    monthlyIncomeCents: number;
    monthlyBillsCents: number;
    monthlySpendCents: number;
    leftForGoalsCents: number;
}

/**
 * Resolves default values for the Money Mapper draft from membership profile data.
 * Priority: connected/detected data first, then detailed income/bills/spend, then 3MN profile answers.
 */
export const resolveMoneyMapperDefaults = (
    membership: MembershipDetails | null,
): ResolvedMoneyMapperDefaults => {
    if (membership == null) {
        return {
            monthlyIncomeCents: 0,
            monthlyBillsCents: 0,
            monthlySpendCents: 0,
            leftForGoalsCents: 0,
        };
    }

    const {
        membershipIncomes,
        bills,
        membershipSpend,
        membershipProfileAnswers,
    } = membership;

    const monthlyIncomeCents = resolveMonthlyIncomeCents(
        membershipIncomes,
        membershipProfileAnswers,
    );
    const monthlyBillsCents = resolveMonthlyBillsCents(
        bills,
        membershipProfileAnswers,
    );
    const monthlySpendCents = resolveMonthlySpendCents(
        membershipSpend,
        monthlyIncomeCents,
        monthlyBillsCents,
    );
    const leftForGoalsCents = Math.max(
        0,
        monthlyIncomeCents - monthlyBillsCents - monthlySpendCents,
    );

    return {
        monthlyIncomeCents,
        monthlyBillsCents,
        monthlySpendCents,
        leftForGoalsCents,
    };
};

const resolveMonthlyIncomeCents = (
    membershipIncomes: MembershipDetails['membershipIncomes'] | undefined,
    membershipProfileAnswers: MembershipDetails['membershipProfileAnswers'],
): number => {
    const fromIncomes = (membershipIncomes ?? []).reduce((total, income) => {
        if (income.monthlyTakeHomeCents != null) {
            return total + income.monthlyTakeHomeCents;
        }
        if (income.weeklyTakeHomeCents != null) {
            return total + income.weeklyTakeHomeCents * 4;
        }
        return total;
    }, 0);

    if (fromIncomes > 0) {
        return roundCentsToWholeDollarCents(fromIncomes);
    }

    const incomeAnswer = findMembershipProfileAnswerByQuestionKey<
        ProfileAnswer,
        MembershipProfileIncomeAnswer
    >(membershipProfileAnswers ?? [], QuestionKey.INCOME);
    const profileIncomeDollars = incomeAnswer?.answer?.answer;
    if (typeof profileIncomeDollars === 'number' && profileIncomeDollars > 0) {
        return roundCentsToWholeDollarCents(
            dollarsToCents(profileIncomeDollars),
        );
    }

    return 0;
};

const resolveMonthlyBillsCents = (
    bills: MembershipDetails['bills'] | undefined,
    membershipProfileAnswers: MembershipDetails['membershipProfileAnswers'],
): number => {
    // Bill.monthlyAmount is in cents
    const fromBills = (bills ?? []).reduce(
        (total, bill) => total + bill.monthlyAmount,
        0,
    );

    if (fromBills > 0) {
        return roundCentsToWholeDollarCents(Math.round(fromBills));
    }

    const billsAnswer = findMembershipProfileAnswerByQuestionKey<
        ProfileAnswer,
        MembershipProfileBillsAnswer
    >(membershipProfileAnswers ?? [], QuestionKey.BILLS);
    const profileBillsDollars = billsAnswer?.answer?.answer;
    if (typeof profileBillsDollars === 'number' && profileBillsDollars > 0) {
        return roundCentsToWholeDollarCents(
            dollarsToCents(profileBillsDollars),
        );
    }

    return 0;
};

const resolveMonthlySpendCents = (
    membershipSpend: MembershipDetails['membershipSpend'] | null | undefined,
    monthlyIncomeCents: number,
    monthlyBillsCents: number,
): number => {
    const spendAmountDollars = membershipSpend?.amount;
    if (spendAmountDollars != null && spendAmountDollars > 0) {
        return roundCentsToWholeDollarCents(dollarsToCents(spendAmountDollars));
    }

    const remainingCents = Math.max(0, monthlyIncomeCents - monthlyBillsCents);
    const halfRemainingCents = remainingCents / 2;
    return roundCentsToWholeDollarCents(halfRemainingCents);
};
